import React from "react";
import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import { Brand, BrandMark } from "../components/Brand";
import { Loader } from "../components/Loader";
import { useAuthHeaders } from "../lib/auth";
import { deviceLink } from "../lib/api";
import { Button, Callout, Card, Icon } from "../design";

type State = "loading" | "confirm" | "done" | "expired" | "missing";

// The onboarding wizard (same origin) records which harness the user picked;
// read it back so the done screen can name it. Absent → generic copy.
const HARNESS_NAMES: Record<string, string> = {
  claude: "Claude Code",
  codex: "Codex",
  opencode: "opencode",
};
function setupTarget(): string | null {
  try {
    const raw = localStorage.getItem("condense.onboarding.v1");
    if (!raw) return null;
    const id = (JSON.parse(raw) as { harnessId?: string }).harnessId;
    return id ? (HARNESS_NAMES[id] ?? null) : null;
  } catch {
    return null;
  }
}

function Pairing({ status }: { status: "waiting" | "linked" | "broken" }) {
  return (
    <div className={`per-pair ${status}`} aria-hidden="true">
      <span className="per-pair-node">
        <Icon name="terminal" size={20} />
      </span>
      <span className="per-pair-link">
        {status !== "waiting" && (
          <span className="per-pair-badge">
            <Icon name={status === "linked" ? "check" : "x"} size={14} />
          </span>
        )}
      </span>
      <span className="per-pair-node">
        <BrandMark size={24} />
      </span>
    </div>
  );
}

function DeviceCode({ code }: { code: string }) {
  return (
    <>
      <span className="ds-sr">{code}</span>
      <div className="per-code" aria-hidden="true">
        {[...code].map((char, i) => (
          <span
            key={i}
            className={char === "-" ? "per-code-sep" : "per-code-char"}
          >
            {char}
          </span>
        ))}
      </div>
    </>
  );
}

export function Cli() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const clerk = useClerk();
  const authHeaders = useAuthHeaders();
  const params = new URLSearchParams(window.location.search);
  const userCode = params.get("code") || "";
  const [state, setState] = React.useState<State>(
    userCode ? "loading" : "missing",
  );
  const [busy, setBusy] = React.useState(false);
  const [failure, setFailure] = React.useState("");

  React.useEffect(() => {
    if (!isLoaded || !userCode) return;
    if (!isSignedIn) {
      void clerk.redirectToSignIn({
        signInForceRedirectUrl: window.location.href,
        signUpForceRedirectUrl: window.location.href,
      });
      return;
    }
    setState((s) => (s === "loading" ? "confirm" : s));
  }, [clerk, isLoaded, isSignedIn, userCode]);

  async function approve() {
    setBusy(true);
    setFailure("");
    try {
      const r = await deviceLink(authHeaders, userCode);
      if (r.ok) setState("done");
      else if (r.status === 404) setState("expired");
      else
        setFailure(
          `Couldn’t link this terminal (error ${r.status}). Try again in a moment.`,
        );
    } catch {
      setFailure(
        "Couldn’t reach condense. Check your connection and try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  const target = setupTarget();
  const email = user?.primaryEmailAddress?.emailAddress;
  const newCode = (
    <>
      Run <code className="mono">dense login</code> in your terminal
    </>
  );

  return (
    <div className="app-shell">
      <header className="app-header">
        <Brand />
      </header>
      <main className="per-cli">
        {state === "loading" ? (
          <Loader />
        ) : (
          <Card as="div" className="per-cli-card">
            {state === "confirm" && (
              <>
                <Pairing status="waiting" />
                <h1 className="per-cli-title">Link this terminal</h1>
                <p className="per-cli-lead">
                  A copy of <code className="mono">dense</code> on your machine
                  is asking to sign in as{" "}
                  {email ? <strong>{email}</strong> : "you"}.
                </p>
                <DeviceCode code={userCode} />
                <p className="per-cli-check">
                  Approve only if this matches the code dense printed.
                </p>
                {failure && (
                  <Callout tone="bad" role="alert">
                    {failure}
                  </Callout>
                )}
                <Button
                  variant="primary"
                  icon="check"
                  busy={busy}
                  className="per-cli-approve"
                  onClick={approve}
                >
                  {busy ? "Approving…" : "Approve"}
                </Button>
              </>
            )}
            {state === "done" && (
              <>
                <Pairing status="linked" />
                <h1 className="per-cli-title">Denser already</h1>
                <p className="per-cli-lead">
                  You&rsquo;re all set up{target ? ` for ${target}` : ""}. Head
                  back to your terminal; you can close this tab.
                </p>
              </>
            )}
            {state === "expired" && (
              <>
                <Pairing status="broken" />
                <h1 className="per-cli-title">This code no longer works</h1>
                <p className="per-cli-lead">
                  It expired or was already used. {newCode} to get a new one.
                </p>
              </>
            )}
            {state === "missing" && (
              <>
                <Pairing status="broken" />
                <h1 className="per-cli-title">This link has no code</h1>
                <p className="per-cli-lead">
                  {newCode} and open the link it prints.
                </p>
              </>
            )}
          </Card>
        )}
      </main>
    </div>
  );
}
